import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import {
  consultarLemit, bestPhone, bestEmail, bestEndereco, phoneDigits, isoToDate, formatCep,
  isLemitLimitError,
} from './lemit';
import {
  LemitTelefone, LemitEmail, LemitEndereco, LemitSocio, LemitEmpresaData, LemitPessoaData,
} from '../../types';
// TS puro, sem Deno — o mesmo mapeador CNAE→segmento que a Busca Geográfica e a edge usam.
import { segmentoDaLinha } from '../../supabase/functions/_shared/segmentoCnae';

// ─────────────────────────────────────────────────────────────────────────────
// Serviço "Lista Fria" (Prospecção) — sobe uma planilha (a chave é só o CPF/CNPJ;
// o resto a Lemit complementa), grava no pool `empresas_prospectadas`
// (fonte='upload'), geocodifica com o Google e enriquece em massa via Lemit
// (CNPJ → sócio majoritário/dono; CPF → a própria pessoa). Tudo persiste para não
// perder ao recarregar. Per-linha aqui; o loop/progresso fica na view.
// ─────────────────────────────────────────────────────────────────────────────

export const onlyDigits = (s: unknown): string => String(s ?? '').replace(/\D/g, '');

export type DocTipo = 'cpf' | 'cnpj';

/** Deduz o tipo de documento pelos dígitos (11 = CPF, senão CNPJ). */
export const docTipoFromDigits = (d: string): DocTipo => (onlyDigits(d).length === 11 ? 'cpf' : 'cnpj');

/** Aplica máscara de CNPJ a 14 dígitos. */
export const maskCnpj = (d: string): string =>
  d.length === 14 ? d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : d;

/** Aplica máscara de CPF a 11 dígitos. */
export const maskCpf = (d: string): string =>
  d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : d;

/**
 * Extrai um SEGMENTO legível do CNAE (atividade principal do CNPJ BIZ) — é o que
 * o usuário entende por "açougue", "padaria", "distribuidora". O CNAE vem como
 * "47.24-5-00-Comércio varejista de carnes - açougues"; tiramos o código e ficamos
 * com a descrição. Sem CNAE, devolve vazio (o chamador cai na natureza jurídica).
 */
export const segmentoDoCnae = (cnae?: string | null): string => {
  const s = String(cnae || '').trim();
  if (!s) return '';
  // remove o código no início: "47.24-5-00-", "4724500-", "47.24-5/00 - " etc.
  const desc = s.replace(/^[\d.\-\/\s]+-\s*/, '').trim();
  return desc || s;
};

/** Máscara de documento (CPF ou CNPJ) conforme o tamanho. */
export const maskDoc = (d?: string | null): string => {
  const s = onlyDigits(d);
  return s.length === 11 ? maskCpf(s) : s.length === 14 ? maskCnpj(s) : (d || '');
};

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface LinhaImport {
  documento: string;       // só dígitos (CPF 11 ou CNPJ 14)
  docTipo: DocTipo;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  cep: string;
  tipoLogradouro: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  estado: string;          // UF
  municipio: string;
  tipo: string;            // MATRIZ / FILIAL
  naturezaJuridica: string;
  telefones: string[];     // dígitos, ex.: ["62993266312", ...]
  linkWhatsapp: string;
  sociosTexto: string;     // CNPJ BIZ traz só nomes; CPFs vêm da Lemit
  enderecoCompleto: string;
  /**
   * Colunas da planilha que não têm campo canônico (ex.: data_abertura,
   * porte_empresa, atividades_principal, regime_tributario). Guardamos porque o
   * export de volta pro CNPJ Biz precisa devolvê-las.
   */
  extras?: Record<string, string>;
}

export interface SocioEnriquecido {
  cpf: string;
  nome?: string;
  participacao?: number;
  data_nascimento?: string;
  telefones: LemitTelefone[];
  emails: LemitEmail[];
  enderecos?: LemitEndereco[];
  /** Retorno completo da Lemit para o CPF — alimenta a seção "dados do representante". */
  pessoa?: LemitPessoaData;
}

export interface EnriquecimentoData {
  empresa?: { telefones: LemitTelefone[]; emails: LemitEmail[]; endereco?: LemitEndereco | null };
  pessoa?: { nome?: string; data_nascimento?: string; telefones: LemitTelefone[]; emails: LemitEmail[]; endereco?: LemitEndereco | null };
  socios?: LemitSocio[];                 // do retorno da empresa (cpf, nome, participacao)
  socios_enriquecidos?: SocioEnriquecido[];
  /** CNAE (ramo de atividade) da empresa — fonte do SEGMENTO real (açougue, padaria…), não a natureza jurídica. */
  cnae?: { numero?: string; descricao?: string; segmento?: string; tipo?: string };
  socio_principal?: { cpf: string; nome?: string; participacao?: number };  // sócio majoritário (ou sugestão)
  representante?: { nome?: string; cpf?: string; data_nascimento?: string }; // vai p/ os campos PJ da negociação
  /** true quando há 2+ sócios: não adivinhamos o representante, a UI pergunta. */
  representante_pendente?: boolean;
  /** Como o representante foi escolhido — ver CriterioRepresentante ('manual' = trocado no modal). */
  representante_criterio?: string;
  /** Veio da lista enxuta (view): faltam os blocos pesados e a linha não pode ser gravada assim. */
  _parcial?: boolean;
  /** Situação do CNPJ conferida no CNPJ.BIZ pela rota ótima. */
  cnpjbiz?: { situacao?: string; verificado_em?: string };
  /** CPFs de sócio que a Lemit respondeu "não encontrado". O 404 é cobrado: nunca repetir. */
  cpfs_nao_encontrados?: string[];
  /** Nenhum sócio está na base da Lemit. A empresa fecha sem o contato do dono. */
  dono_nao_encontrado?: boolean;
  melhor_whatsapp?: string;              // dígitos (telefone do dono/empresa/pessoa)
  melhor_email?: string;
  // Busca reversa nome/cidade → CNPJ (Casa dos Dados) na Prospecção Geográfica.
  // Tipado como any p/ evitar dependência circular com o service descobrirCnpj.
  cnpj_descoberta?: {
    consultado_em: string;
    estrategia: string;
    total: number;
    candidatos: any[];
    escolhido?: any;
    escolhido_por?: 'auto' | 'manual';
  };
}

export type EnriquecimentoStatus = 'nao' | 'empresa' | 'socios' | 'completo' | 'falhou';

/** Linha encenada no pool `empresas_prospectadas` (fonte='upload'). */
export interface StagedRow {
  id: string;                   // uuid
  place_id: string;             // 'upload:'+documento
  cnpj: string;                 // legado: espelha `documento` (compat + índice)
  documento?: string;           // CPF ou CNPJ (só dígitos)
  doc_tipo?: DocTipo;
  nome: string;
  razao_social: string;
  categoria: string;
  endereco: string;
  cidade: string | null;
  estado: string | null;
  bairro: string | null;
  cep: string | null;
  email: string | null;
  telefone: string | null;
  latitude: number | null;
  longitude: number | null;
  status: string;               // 'disponivel' | 'importado'
  negociacao_id: number | null;
  importado_por?: string | null;
  import_erro?: string | null;  // mensagem do último erro de import (null = ok)
  kwh_estimado?: number | null;
  rating?: number | null;
  enriquecimento_status: EnriquecimentoStatus;
  enriquecimento_data: EnriquecimentoData | null;
  raw_payload: any;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DonoInfo {
  negociacao_id: number;
  responsavel_nome: string | null;
}

// ── Parsing da planilha (só o documento é obrigatório) ──────────────────────────

const normHeader = (h: string): string =>
  String(h || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // tira acentos
    .toLowerCase().trim().replace(/[\s/]+/g, '_').replace(/[^a-z0-9_]/g, '');

// header normalizado → campo canônico. `documento` aceita várias formas: a única
// coluna obrigatória é o CPF/CNPJ; todo o resto é opcional (a Lemit complementa).
type CanonKey = keyof LinhaImport;
const HEADER_ALIASES: Record<string, CanonKey> = {
  documento: 'documento', cpf_cnpj: 'documento', cpfcnpj: 'documento', doc: 'documento',
  cnpj: 'documento', cpf: 'documento', cnpj_cpf: 'documento',
  razao_social: 'razaoSocial', razaosocial: 'razaoSocial', razao: 'razaoSocial', nome: 'razaoSocial',
  nome_fantasia: 'nomeFantasia', fantasia: 'nomeFantasia', nome_fantazia: 'nomeFantasia',
  email: 'email', e_mail: 'email',
  cep: 'cep',
  descricao_tipo_logradouro: 'tipoLogradouro', tipo_logradouro: 'tipoLogradouro',
  logradouro: 'logradouro', endereco: 'logradouro',
  numero: 'numero', n: 'numero', num: 'numero',
  complemento: 'complemento',
  bairro: 'bairro',
  estado: 'estado', uf: 'estado',
  municipio: 'municipio', cidade: 'municipio',
  tipo: 'tipo',
  natureza_juridica: 'naturezaJuridica',
  telefones: 'telefones', telefone: 'telefones', fone: 'telefones', whatsapp_telefone: 'telefones',
  link_whatsapp_cnpjbiz: 'linkWhatsapp', link_whatsapp: 'linkWhatsapp', whatsapp: 'linkWhatsapp',
  socios: 'sociosTexto', socio: 'sociosTexto',
};

const parseTelefones = (raw: unknown): string[] => {
  if (!raw) return [];
  return String(raw)
    .split(/[;,/\n]+/)
    .map(onlyDigits)
    .filter((d) => d.length >= 10 && d.length <= 11);
};

const montarEndereco = (l: Partial<LinhaImport>): string => {
  const rua = [l.tipoLogradouro, l.logradouro].filter(Boolean).join(' ').trim();
  const linha1 = [rua, l.numero].filter(Boolean).join(', ');
  return [linha1, l.complemento, l.bairro, [l.municipio, l.estado].filter(Boolean).join(' - '), l.cep]
    .map((s) => (s || '').toString().trim())
    .filter(Boolean)
    .join(', ');
};

/** Lê um arquivo xlsx/csv e devolve as linhas mapeadas (só com documento CPF/CNPJ válido). */
export async function parsePlanilha(file: File): Promise<LinhaImport[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

  const vistos = new Set<string>();
  return rows
    .map((row) => {
      const l: Partial<LinhaImport> = {};
      const extras: Record<string, string> = {};
      for (const [key, val] of Object.entries(row)) {
        const canon = HEADER_ALIASES[normHeader(key)];
        if (!canon) {
          // Sem campo canônico: preserva p/ o export de volta ao CNPJ Biz.
          const v = String(val ?? '').trim();
          if (v) extras[normHeader(key)] = v;
          continue;
        }
        if (canon === 'telefones') l.telefones = parseTelefones(val);
        else if (canon === 'documento') { if (!l.documento) l.documento = onlyDigits(val); }
        else if (!(l as any)[canon]) (l as any)[canon] = String(val ?? '').trim();
      }
      if (Object.keys(extras).length) l.extras = extras;
      l.telefones = l.telefones || [];
      l.enderecoCompleto = montarEndereco(l);
      if (l.documento) l.docTipo = docTipoFromDigits(l.documento);
      return l as LinhaImport;
    })
    .filter((l) => {
      const d = onlyDigits(l.documento);
      if (d.length !== 11 && d.length !== 14) return false;
      if (vistos.has(d)) return false;   // dedup dentro da própria planilha
      vistos.add(d);
      return true;
    });
}

/** Gera e baixa uma planilha-modelo — a chave é só o CPF/CNPJ (o resto é opcional). */
export function baixarPlanilhaModelo(): void {
  const headers = ['cpf_cnpj', 'nome', 'telefone'];
  const exemplos = [
    ['00000000000191', 'Empresa Exemplo LTDA (opcional)', '(62) 99999-0000'],
    ['12345678909', 'Fulano de Tal (opcional)', '(62) 98888-0000'],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...exemplos]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Lista');
  XLSX.writeFile(wb, 'modelo_lista_fria_vigorhub.xlsx');
}

// ── Duplicidade / dono no CRM ──────────────────────────────────────────────────

/**
 * Mapa documento(dígitos) → dono no CRM (negociação já existente). Aceita CPF e CNPJ.
 * Busca TODAS as negociações uma vez (paginado) e casa localmente por dígitos — evita
 * centenas de round-trips quando a lista fria tem >10k documentos.
 */
export async function checarDuplicidade(
  docs: string[],
  /**
   * documento → id da negociação que a PRÓPRIA linha criou ao ser importada.
   * Sem isso, todo lead importado por nós aparecia como "duplicado no CRM" — ele
   * encontrava a negociação que ele mesmo gerou (495 falsos positivos de 505).
   */
  proprias: Record<string, number | null> = {},
): Promise<Record<string, DonoInfo>> {
  const alvos = new Set(docs.map(onlyDigits).filter((d) => d.length === 11 || d.length === 14));
  if (alvos.size === 0) return {};

  // 1) Carrega o índice documento→negociações de todo o CRM (paginado).
  //    Guardamos TODAS as negociações do documento para conseguir descartar a própria.
  const PAGE = 1000;
  const crm = new Map<string, Array<{ negociacao_id: number; responsavel_id: string | null }>>();
  const respIds = new Set<string>();
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('negociacoes')
      .select('id, cpf_cnpj, responsavel_id')
      .not('cpf_cnpj', 'is', null)
      // `.range()` sem ORDER é paginação instável: páginas se sobrepõem/pulam e
      // negociações somem do índice → duplicata real passa como "novo". `id`
      // (único) torna a varredura determinística.
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data || []) as any[];
    rows.forEach((n) => {
      const d = onlyDigits(n.cpf_cnpj);
      if (d.length !== 11 && d.length !== 14) return;
      const lista = crm.get(d) || [];
      lista.push({ negociacao_id: n.id, responsavel_id: n.responsavel_id ?? null });
      crm.set(d, lista);
      if (n.responsavel_id) respIds.add(String(n.responsavel_id));
    });
    if (rows.length < PAGE) break;
  }

  // 2) Nomes dos responsáveis dos donos encontrados.
  const nomes: Record<string, string> = {};
  const idsArr = Array.from(respIds);
  for (let i = 0; i < idsArr.length; i += 200) {
    const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', idsArr.slice(i, i + 200));
    (profs || []).forEach((p: any) => { nomes[String(p.id)] = p.full_name; });
  }

  // 3) Resolve só os documentos pedidos — descartando a negociação criada pela
  //    própria linha (só é duplicata se houver OUTRA negociação com o documento).
  const out: Record<string, DonoInfo> = {};
  alvos.forEach((d) => {
    const propria = proprias[d] ?? null;
    const hit = (crm.get(d) || []).find((n) => n.negociacao_id !== propria);
    if (hit) out[d] = { negociacao_id: hit.negociacao_id, responsavel_nome: hit.responsavel_id ? (nomes[String(hit.responsavel_id)] || null) : null };
  });
  return out;
}

// ── Staging no pool ────────────────────────────────────────────────────────────

/**
 * Cidade na forma canônica da base — a que JÁ existe no pool com acento.
 *
 * O CNPJ BIZ manda o município sem acento ("Goiania") e a busca do Google manda com
 * ("Goiânia"). Como o filtro da tela compara string exata, isso partia a base em duas:
 * 2.085 leads em "Goiânia" e 643 em "Goiania" — e cruzar cidade com segmento devolvia
 * zero, porque o enriquecimento tinha caído todo numa das metades.
 */
const CIDADES_CANONICAS: Record<string, string> = {};
const chaveCidade = (c: string) =>
  c.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

export const registrarCidadesCanonicas = (cidades: Array<string | null | undefined>) => {
  for (const c of cidades) {
    if (!c) continue;
    const k = chaveCidade(c);
    // Entre variantes, vence a acentuada (mais informativa)
    if (!CIDADES_CANONICAS[k] || (/[^\x00-\x7F]/.test(c) && !/[^\x00-\x7F]/.test(CIDADES_CANONICAS[k]))) {
      CIDADES_CANONICAS[k] = c;
    }
  }
};

export const cidadeCanonica = (cidade?: string | null): string | null => {
  if (!cidade) return null;
  return CIDADES_CANONICAS[chaveCidade(cidade)] || cidade;
};

const linhaToStagingInsert = (l: LinhaImport) => ({
  place_id: `upload:${l.documento}`,
  nome: l.nomeFantasia || l.razaoSocial || maskDoc(l.documento),
  // Segmento = atividade principal (CNAE); só cai na natureza jurídica se não houver CNAE.
  categoria: segmentoDoCnae(l.extras?.atividades_principal) || l.naturezaJuridica || 'Lista Fria',
  endereco: l.enderecoCompleto || null,
  cidade: cidadeCanonica(l.municipio),
  estado: l.estado || null,
  bairro: l.bairro || null,
  cep: l.cep || null,
  email: l.email || null,
  telefone: l.telefones[0] || null,
  documento: l.documento,
  doc_tipo: l.docTipo,
  cnpj: l.documento,          // legado: espelha o documento (compat + índice existente)
  fonte: 'upload',
  status: 'disponivel',
  raw_payload: l,
});

/** Insere as novas linhas (sem clobber das já existentes) e devolve o pool de upload atual. */
export async function gravarStaging(linhas: LinhaImport[]): Promise<StagedRow[]> {
  if (linhas.length) {
    const inserts = linhas.map(linhaToStagingInsert);
    // ignoreDuplicates: mantém intactas as linhas já gravadas (preserva enriquecimento/import)
    for (let i = 0; i < inserts.length; i += 500) {
      const { error } = await supabase
        .from('empresas_prospectadas')
        .upsert(inserts.slice(i, i + 500), { onConflict: 'place_id', ignoreDuplicates: true });
      if (error) throw error;
    }

    // Refresca o SEGMENTO (categoria) das linhas que já existiam — assim uma lista
    // resubida com o CNAE corrige o segmento sem tocar em status/enriquecimento.
    // Agrupa por valor de categoria p/ atualizar em 1 query por segmento distinto.
    const porCategoria = new Map<string, string[]>();
    inserts.forEach((r) => {
      if (!r.categoria || r.categoria === 'Lista Fria') return;
      const arr = porCategoria.get(r.categoria) || [];
      arr.push(r.place_id);
      porCategoria.set(r.categoria, arr);
    });
    for (const [categoria, placeIds] of porCategoria) {
      for (let i = 0; i < placeIds.length; i += 300) {
        await supabase.from('empresas_prospectadas')
          .update({ categoria })
          .in('place_id', placeIds.slice(i, i + 300));
      }
    }

    // Mesma ideia p/ o NOME — só refresca quando o upload traz um nome real (nome
    // fantasia ou razão social), nunca o fallback (documento mascarado); assim uma
    // lista resubida corrige um nome que hoje é só o CPF/CNPJ mascarado, sem
    // sobrescrever um nome bom já gravado por um upload/enriquecimento anterior.
    const porNome = new Map<string, string[]>();
    inserts.forEach((r, i) => {
      const l = linhas[i];
      const nomeReal = l.nomeFantasia || l.razaoSocial;
      if (!nomeReal) return;
      const arr = porNome.get(r.nome) || [];
      arr.push(r.place_id);
      porNome.set(r.nome, arr);
    });
    for (const [nome, placeIds] of porNome) {
      for (let i = 0; i < placeIds.length; i += 300) {
        await supabase.from('empresas_prospectadas')
          .update({ nome })
          .in('place_id', placeIds.slice(i, i + 300));
      }
    }
  }
  return carregarStaging();
}

const STAGING_COLS =
  'id, place_id, cnpj, documento, doc_tipo, nome, categoria, endereco, cidade, estado, bairro, cep, email, telefone, latitude, longitude, status, negociacao_id, importado_por, import_erro, kwh_estimado, rating, enriquecimento_status, enriquecimento_data, raw_payload, created_at, updated_at';

// Lista enxuta (view `empresas_prospectadas_lista`): o JSON da Lemit vem sem os blocos
// pesados e marcado com `_parcial`. Sem `raw_payload` — a razão social vem à parte.
const LISTA_COLS =
  'id, place_id, cnpj, documento, doc_tipo, nome, categoria, endereco, cidade, estado, bairro, cep, email, telefone, latitude, longitude, status, negociacao_id, importado_por, import_erro, kwh_estimado, rating, enriquecimento_status, enriquecimento_data, razao_social_planilha, created_at, updated_at';

const mapStaged = (r: any): StagedRow => ({
  ...r,
  documento: r.documento ?? r.cnpj,
  doc_tipo: r.doc_tipo ?? (onlyDigits(r.documento ?? r.cnpj).length === 11 ? 'cpf' : 'cnpj'),
  razao_social: r.razao_social_planilha || r.raw_payload?.razaoSocial || r.nome,
  raw_payload: r.raw_payload ?? null,
});

/** A linha veio da lista enxuta? Então falta parte do JSON da Lemit e ela não pode ser gravada. */
export const linhaParcial = (row: { enriquecimento_data?: EnriquecimentoData | null } | null | undefined): boolean =>
  !!row?.enriquecimento_data?._parcial;

/** Tentativa de gravar a versão enxuta — apagaria o que ficou de fora dela. */
export class LinhaParcialError extends Error {
  constructor() {
    super('Linha incompleta da Lista Fria: recarregue a página antes de alterar este lead.');
    this.name = 'LinhaParcialError';
  }
}

/**
 * Linhas COMPLETAS (JSON da Lemit inteiro) de uma lista de place_ids.
 *
 * As telas carregam o pool em versão enxuta (o JSON inteiro tem ~5 KB por linha) e têm
 * que passar por aqui antes de enriquecer, importar ou exportar: os patches são
 * montados como `{...linha, ...novo}` e, sobre a versão enxuta, apagariam os sócios e o
 * CNAE gravados. 4 lotes de 100 em paralelo — o "Exportar tudo" pede milhares.
 */
export async function carregarStagedPorPlaceIds(placeIds: string[]): Promise<StagedRow[]> {
  const ids = Array.from(new Set(placeIds.filter(Boolean)));
  const lotes: string[][] = [];
  for (let i = 0; i < ids.length; i += 100) lotes.push(ids.slice(i, i + 100));
  const acc: StagedRow[] = [];
  for (let i = 0; i < lotes.length; i += 4) {
    const respostas = await Promise.all(lotes.slice(i, i + 4).map((lote) =>
      supabase.from('empresas_prospectadas').select(STAGING_COLS).in('place_id', lote)));
    for (const { data, error } of respostas) {
      if (error) throw error;
      (data || []).forEach((r: any) => acc.push(mapStaged(r)));
    }
  }
  return acc;
}

/** A linha completa quando ela veio enxuta; senão a própria linha. */
export async function garantirCompleta<T extends StagedRow>(row: T): Promise<T> {
  if (!linhaParcial(row)) return row;
  const [completa] = await carregarStagedPorPlaceIds([row.place_id]);
  if (!completa) throw new Error('Lead não encontrado na Lista Fria.');
  return { ...row, ...completa };
}

/** Mesmo que `garantirCompleta`, para um lote (import em massa, exports). */
export async function hidratarLinhas<T extends StagedRow>(rows: T[]): Promise<T[]> {
  const parciais = rows.filter(linhaParcial);
  if (parciais.length === 0) return rows;
  const completas = new Map((await carregarStagedPorPlaceIds(parciais.map((r) => r.place_id))).map((r) => [r.place_id, r]));
  return rows.map((r) => (linhaParcial(r) && completas.has(r.place_id) ? { ...r, ...completas.get(r.place_id)! } : r));
}

export interface CarregarStagingFiltro { status?: string; }

/**
 * Carrega o pool de lista fria (fonte='upload'), paginando com `.range()` — o
 * PostgREST corta cada resposta em 1000 linhas, então iteramos até esgotar
 * (hoje o pool tem >10k linhas).
 *
 * IMPORTANTE: ordenar SÓ por `created_at` é instável — quase todo o pool foi
 * inserido no mesmo lote e ~10k linhas dividem o mesmo timestamp. Sem uma chave
 * única de desempate, o LIMIT/OFFSET do Postgres devolve as linhas empatadas em
 * ordem indefinida, então páginas adjacentes se sobrepõem/pulam registros → a
 * lista montada ganha place_id duplicado (chave React colidida: clicar "não faz
 * nada" e a linha não atualiza) e some com outros. `id` (uuid, único) como
 * desempate torna a paginação determinística. O dedup por place_id é uma rede de
 * segurança extra (nenhuma chave repetida chega ao React).
 *
 * Lê a view enxuta (64 MB → 14 MB). As linhas voltam com `_parcial`: quem for
 * gravar passa por `garantirCompleta`/`hidratarLinhas`. Sem a view no banco (migration
 * 20261025120000 ainda não aplicada), cai na tabela com o JSON inteiro, como antes.
 *
 * Paginação por CHAVE (`id > último`), não por OFFSET: a view monta o JSON enxuto linha
 * a linha e, com OFFSET, cada página remontava todas as que pulava — a 11ª página levava
 * 1,1 s no banco contra 0,15 s pela chave, e a aba ficou mais lenta que antes (18 s).
 * A ordem da tela (mais recentes primeiro, `id` como desempate) é refeita aqui.
 */
export async function carregarStaging(filtro: CarregarStagingFiltro = {}): Promise<StagedRow[]> {
  const PAGE = 1000;
  const acc: StagedRow[] = [];
  const vistos = new Set<string>();
  let fonte: { tabela: string; cols: string } = { tabela: 'empresas_prospectadas_lista', cols: LISTA_COLS };
  let ultimoId: string | null = null;
  for (;;) {
    const pagina = () => {
      let query = supabase
        .from(fonte.tabela)
        .select(fonte.cols)
        .eq('fonte', 'upload');
      if (filtro.status) query = query.eq('status', filtro.status);
      if (ultimoId) query = query.gt('id', ultimoId);
      return query.order('id', { ascending: true }).limit(PAGE);
    };
    let { data, error } = await pagina();
    if (error && !ultimoId && (error.code === 'PGRST205' || error.code === '42P01')) {
      fonte = { tabela: 'empresas_prospectadas', cols: STAGING_COLS };
      ({ data, error } = await pagina());
    }
    if (error) throw error;
    const rows = (data || []) as any[];
    for (const r of rows) {
      if (vistos.has(r.place_id)) continue;
      vistos.add(r.place_id);
      acc.push(mapStaged(r));
    }
    if (rows.length < PAGE) break;
    ultimoId = rows[rows.length - 1].id;
  }
  // Comparação de string pura (não localeCompare): o `-` do uuid e do timestamp não pode
  // ser ignorado pela colação do idioma.
  const cmp = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);
  acc.sort((a, b) => cmp(String(b.created_at || ''), String(a.created_at || '')) || cmp(a.id, b.id));
  // Alimenta o dicionário de cidades canônicas com o que a base já tem. Sem esta
  // chamada `CIDADES_CANONICAS` fica vazio e `cidadeCanonica()` vira função de
  // identidade — a proteção contra "Goiânia"/"Goiania" existia mas nunca rodava.
  registrarCidadesCanonicas(acc.map((r) => r.cidade));
  return acc;
}

// ── Geocode (Google) ───────────────────────────────────────────────────────────

/** Melhor endereço que a Lemit gravou no JSON da linha (empresa antes de pessoa). */
export function enderecoLemitDaRow(row: Pick<StagedRow, 'enriquecimento_data'>): LemitEndereco | null {
  const enr = row.enriquecimento_data;
  return enr?.empresa?.endereco || enr?.pessoa?.endereco || null;
}

/**
 * Texto que vai para o Google.
 *
 * As colunas vêm primeiro, mas o JSON da Lemit é um fallback necessário: a etapa 1
 * só passou a copiar o endereço para as colunas em 17/07/2026, então as linhas
 * enriquecidas antes disso têm endereço no `enriquecimento_data` e coluna vazia —
 * e o lote as pulava caladas, "achando tudo" sem geocodificar nada.
 */
export function enderecoParaGeocode(row: StagedRow): string {
  const daColuna = String(row.endereco || '').trim();
  if (daColuna.length >= 3) return daColuna;
  const doLemit = enderecoLemitToString(enderecoLemitDaRow(row));
  if (doLemit.length >= 3) return doLemit;
  return `${row.nome || ''}, ${row.cidade || ''} ${row.estado || ''}`.trim();
}

/** Dá para localizar esta linha no mapa? `false` = só temos o documento dela. */
export const temEnderecoParaGeocode = (row: StagedRow): boolean =>
  enderecoParaGeocode(row).replace(/[\s,]/g, '').length >= 3;

/**
 * A coordenada caiu dentro do Brasil?
 *
 * O endereço da Lemit chega sujo com frequência — tipo de logradouro duplicado
 * ("AV AV  PORTUGAL") e acento corrompido ("CERQUEIRA C+SAR"). Com isso o Google
 * desiste do endereço e devolve o melhor palpite global: 12 leads foram parar na
 * Polônia, um deles a "CLINICA SALES" do Setor Marista, que virou um pin perdido no
 * meio de um parque polonês. Coordenada fora do país é sempre erro de geocode —
 * melhor ficar sem localização do que com uma errada, que envenena raio e rota.
 */
export const dentroDoBrasil = (lat: number, lng: number): boolean =>
  lat >= -34 && lat <= 6 && lng >= -74 && lng <= -34;

/**
 * O geocode caiu na UF que o cadastro diz?
 *
 * Checar só o país não basta: endereço ilegível ("R R SOUZ-NIA", "INDEPEND-NCIA") faz o
 * Google devolver um palpite qualquer DENTRO do Brasil — 135 leads de Goiás foram parar
 * no mesmo ponto em Roraima, a 2.500 km, e passaram pela validação de país porque
 * Roraima é Brasil. A UF é o corte que pega esse caso.
 *
 * Sem UF no cadastro ou sem UF na resposta, aceita (não dá para afirmar que está errado).
 */
export const ufConfere = (ufCadastro?: string | null, ufGeocode?: string | null): boolean => {
  const a = String(ufCadastro || '').trim().toUpperCase();
  const b = String(ufGeocode || '').trim().toUpperCase();
  if (a.length !== 2 || b.length !== 2) return true;
  return a === b;
};

/** Geocodifica um endereço completo via edge function geocode-search (Google). */
export async function geocodificarRow(row: StagedRow): Promise<{ lat: number | null; lng: number | null; status: string }> {
  const { data, error } = await supabase.functions.invoke('geocode-search', {
    body: { action: 'geocode', address: enderecoParaGeocode(row) },
  });
  if (error) throw error;
  let lat = data?.lat ?? null;
  let lng = data?.lng ?? null;
  let motivo = '';
  if (lat != null && lng != null && !dentroDoBrasil(Number(lat), Number(lng))) {
    motivo = 'FORA_DO_BRASIL';
  } else if (lat != null && lng != null && !ufConfere(row.estado, data?.uf)) {
    motivo = 'UF_DIVERGENTE';
  }
  if (motivo) {
    console.warn(`Geocode descartado (${motivo}) para ${row.nome}: ${lat},${lng} · UF cadastro=${row.estado} × geocode=${data?.uf} · ${enderecoParaGeocode(row)}`);
    lat = null;
    lng = null;
  }
  if (lat != null && lng != null) {
    await supabase.from('empresas_prospectadas')
      .update({ latitude: lat, longitude: lng, updated_at: new Date().toISOString() })
      .eq('place_id', row.place_id);
  }
  return { lat, lng, status: motivo || (data?.status || 'UNKNOWN') };
}

// ── Enriquecimento Lemit ───────────────────────────────────────────────────────

async function salvarEnriquecimento(placeId: string, patch: Partial<StagedRow>): Promise<void> {
  // Mesma trava do gatilho do banco, do lado de cá: patch montado sobre a lista enxuta
  // apagaria os sócios e o CNAE. E o erro do update deixa de passar calado.
  if (linhaParcial(patch)) throw new LinhaParcialError();
  const { error } = await supabase.from('empresas_prospectadas')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('place_id', placeId);
  if (error) throw new Error(error.message);
}

/** Documento + tipo de uma linha, robusto a rows vindas da Busca Geográfica (só `cnpj`). */
function docDaRow(row: StagedRow): { doc: string; tipo: DocTipo } {
  const doc = onlyDigits(row.documento || row.cnpj);
  return { doc, tipo: row.doc_tipo || docTipoFromDigits(doc) };
}

/** Endereço da Lemit numa linha só ("R DEPUTADO X, 280, CENTRO, JATAI - GO, 75803-017"). */
export function enderecoLemitToString(end?: LemitEndereco | null): string {
  if (!end) return '';
  const via = String(end.logradouro || end.endereco || '').trim();
  const tipo = String(end.tipo_logradouro || '').trim();
  // A Lemit repete o tipo dentro do logradouro ("AV" + "AV  CONTORNO"): só prefixa
  // quando ele ainda não está lá, senão o endereço sai "AV AV CONTORNO".
  const linha = tipo && !via.toUpperCase().startsWith(`${tipo.toUpperCase()} `) ? `${tipo} ${via}`.trim() : via;
  return [
    [linha, end.numero].filter(Boolean).join(', '),
    end.complemento, end.bairro,
    [end.cidade, end.uf].filter(Boolean).join(' - '),
    formatCep(end.cep),
  ].map((s) => String(s || '').trim()).filter(Boolean).join(', ');
}

/** Converte um endereço da Lemit num patch de colunas do staged row (p/ Rotas ler o endereço). */
function enderecoPatch(end?: LemitEndereco | null): Partial<StagedRow> {
  if (!end) return {};
  const enderecoStr = enderecoLemitToString(end);
  return {
    // Canoniza: a Lemit devolve a cidade na grafia dela ("GOIANIA"), e gravar isso cru
    // recriava a divisão que `cidadeCanonica` existe para fechar — o enriquecimento
    // arrastava a linha para uma segunda "Goiania" e ela sumia do filtro da tela.
    cidade: cidadeCanonica(end.cidade) || undefined,
    bairro: end.bairro || undefined,
    cep: end.cep ? formatCep(end.cep) : undefined,
    estado: end.uf || undefined,
    endereco: enderecoStr || undefined,
  };
}

/** Etapa 1 — enriquece o DOCUMENTO via Lemit. CNPJ → empresa+sócios; CPF → a pessoa. */
export async function enriquecerEmpresaRow(rowTela: StagedRow): Promise<StagedRow> {
  const row = await garantirCompleta(rowTela);
  const { doc, tipo } = docDaRow(row);
  try {
    if (tipo === 'cpf') {
      const r = await consultarLemit({ criterio: 'cpf', valor: doc });
      const d = r.data as LemitPessoaData;
      const telBest = bestPhone(d.celulares, d.fixos);
      const mailBest = bestEmail(d.emails);
      const endBest = bestEndereco(d.enderecos);
      const enr: EnriquecimentoData = {
        ...(row.enriquecimento_data || {}),
        pessoa: {
          nome: d.nome, data_nascimento: d.data_nascimento,
          telefones: [...(d.celulares || []), ...(d.fixos || [])], emails: d.emails || [], endereco: endBest,
        },
        melhor_whatsapp: phoneDigits(telBest) || row.enriquecimento_data?.melhor_whatsapp,
        melhor_email: mailBest?.email || row.enriquecimento_data?.melhor_email,
      };
      const patch: Partial<StagedRow> = {
        nome: d.nome || row.nome,
        telefone: phoneDigits(telBest) || row.telefone,
        email: mailBest?.email || row.email,
        ...enderecoPatch(endBest),
        enriquecimento_data: enr,
        enriquecimento_status: 'completo', // CPF não tem sócios: já é o contato final
      };
      await salvarEnriquecimento(row.place_id, patch);
      return { ...row, ...patch };
    }

    // CNPJ → empresa
    const r = await consultarLemit({ criterio: 'cnpj', valor: doc });
    const d = r.data as LemitEmpresaData;
    const telBest = bestPhone(d.celulares, d.fixos);
    const mailBest = bestEmail(d.emails);
    const enr: EnriquecimentoData = {
      ...(row.enriquecimento_data || {}),
      empresa: { telefones: [...(d.celulares || []), ...(d.fixos || [])], emails: d.emails || [], endereco: d.endereco || null },
      socios: d.socios || [],
      cnae: (d.cnae as any) || row.enriquecimento_data?.cnae,   // ramo de atividade → segmento real
      melhor_whatsapp: phoneDigits(telBest) || row.enriquecimento_data?.melhor_whatsapp,
      melhor_email: mailBest?.email || row.enriquecimento_data?.melhor_email,
    };
    // Só deixa a Lemit corrigir o nome quando o atual ainda é o placeholder
    // (documento mascarado) — preserva um nome fantasia/razão social já bom.
    const nomeEhPlaceholder = row.nome === maskDoc(row.documento);
    const patch: Partial<StagedRow> = {
      nome: (nomeEhPlaceholder && (d.nome_fantasia || d.razao_social)) || row.nome,
      telefone: phoneDigits(telBest) || row.telefone,
      email: mailBest?.email || row.email,
      ...enderecoPatch(d.endereco),
      enriquecimento_data: enr,
      enriquecimento_status: 'empresa',
    };
    await salvarEnriquecimento(row.place_id, patch);
    return { ...row, ...patch };
  } catch (e: any) {
    // Limite (teto diário / rate-limit) NÃO é falha do documento: não marca 'falhou'
    // (senão um CNPJ válido vira lixo permanente) — só propaga p/ o lote parar e
    // ser retentado quando o limite renovar.
    if (!isLemitLimitError(e?.message) && !(e instanceof LinhaParcialError)) {
      await salvarEnriquecimento(row.place_id, { enriquecimento_status: 'falhou' });
    }
    throw e;
  }
}

// ⚠️ Espelho de supabase/functions/_shared/lemitEnriquecimento.ts — mexeu aqui,
// mexa lá (o worker da fila usa a versão Deno). O teste do _shared trava a regra.

export type CriterioRepresentante =
  | 'nenhum'              // nenhum sócio utilizável
  | 'unico'               // só havia um
  | 'maior_participacao'  // a Lemit informou o rateio e há um maior
  | 'maior_capital'       // desempate por capital social
  | 'ordem_lemit'         // empate real — ficou o primeiro que a Lemit devolveu
  | 'proximo_socio';      // o escolhido não está na Lemit — ficou o seguinte na mesma ordem

export interface EscolhaRepresentante {
  socio: LemitSocio | null;
  /** Havia mais de um candidato: a escolha foi automática e a UI deixa trocar. */
  automatica: boolean;
  criterio: CriterioRepresentante;
}

const maioresPor = (lista: LemitSocio[], valor: (s: LemitSocio) => number): LemitSocio[] => {
  const max = lista.reduce((m, s) => Math.max(m, valor(s)), -Infinity);
  return lista.filter((s) => valor(s) === max);
};

const numeroOuZero = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Escolhe o representante legal SOZINHO (pedido de 07/08/2026 — reverte a regra
 * de 17/07 que parava e perguntava sempre que havia 2+ sócios):
 *
 *   1. maior participação, quando a Lemit informou o rateio;
 *   2. desempate por capital social;
 *   3. empate real → o primeiro que a Lemit devolveu.
 *
 * O passo 3 é **estável de propósito**, não sorteado: um sorteio faria uma
 * re-execução escolher outro sócio e pagar uma segunda consulta pela mesma
 * empresa. Na prática dá no mesmo ("tanto faz qual, pega um").
 *
 * Por que o passo 3 é tão comum: quando a Lemit não sabe o rateio ela devolve
 * `participacao: 0` em TODOS os sócios (e repete o capital social da empresa em
 * cada um) — 521 das 778 empresas com 2+ sócios estavam assim. Não é empate
 * societário, é ausência de dado.
 */
export function escolherRepresentante(socios: LemitSocio[]): EscolhaRepresentante {
  const comCpf = socios.filter((s) => onlyDigits(s.cpf).length === 11);
  const base = comCpf.length ? comCpf : socios;

  if (base.length === 0) return { socio: null, automatica: false, criterio: 'nenhum' };
  if (base.length === 1) return { socio: base[0], automatica: false, criterio: 'unico' };

  const porParticipacao = maioresPor(base, (s) => numeroOuZero(s.participacao));
  if (numeroOuZero(porParticipacao[0]?.participacao) > 0 && porParticipacao.length === 1) {
    return { socio: porParticipacao[0], automatica: true, criterio: 'maior_participacao' };
  }
  const candidatos = numeroOuZero(porParticipacao[0]?.participacao) > 0 ? porParticipacao : base;

  const porCapital = maioresPor(candidatos, (s) => numeroOuZero(s.capital_social));
  if (porCapital.length === 1 && numeroOuZero(porCapital[0].capital_social) > 0) {
    return { socio: porCapital[0], automatica: true, criterio: 'maior_capital' };
  }

  return { socio: candidatos[0], automatica: true, criterio: 'ordem_lemit' };
}

/** Sócio majoritário — atalho para `escolherRepresentante(...).socio`. */
export function socioMajoritario(socios: LemitSocio[]): LemitSocio | null {
  return escolherRepresentante(socios).socio;
}

/**
 * Sócios na ordem em que vale consultar o CPF: o que `escolherRepresentante`
 * escolhe e, se a Lemit não o conhecer, o próximo pela mesma regra. Quem já deu
 * "não encontrado" fica de fora — o 404 é cobrado.
 */
export function candidatosDono(
  socios: LemitSocio[],
  naoEncontrados: string[] = [],
): { socio: LemitSocio; criterio: CriterioRepresentante }[] {
  const fora = new Set(naoEncontrados.map(onlyDigits));
  const vistos = new Set<string>();
  let restantes = socios.filter((s) => {
    const cpf = onlyDigits(s.cpf);
    if (cpf.length !== 11 || fora.has(cpf) || vistos.has(cpf)) return false;
    vistos.add(cpf);
    return true;
  });
  const out: { socio: LemitSocio; criterio: CriterioRepresentante }[] = [];
  while (restantes.length) {
    const { socio, criterio } = escolherRepresentante(restantes);
    if (!socio) break;
    out.push({ socio, criterio: out.length || fora.size ? 'proximo_socio' : criterio });
    restantes = restantes.filter((s) => s !== socio);
  }
  return out;
}

/** 404 ("não encontrado") ou 422 (documento inválido): a Lemit nunca vai achar esse CPF. */
const lemitNaoAcha = (msg?: string | null): boolean =>
  /n[aã]o encontrado|not found|\[404\]|\[422\]|deve ser um (cpf|cnpj) v[aá]lido/i.test(String(msg || ''));

/**
 * Próximo passo do enriquecimento de uma linha — fonte única de verdade para os
 * rótulos/botões da UI (lista e modal), para não divergirem.
 *
 * Independe de a linha já ter sido importada: enriquecer e importar são coisas
 * separadas (um lead importado ainda pode precisar do CPF do dono).
 */
export type ProximoPasso = 'enriquecer' | 'buscar_dono' | 'tentar_novamente' | 'completo';

/** Os dados PESSOAIS do CPF `cpf` já foram consultados (telefone/e-mail/payload)? */
function donoConsultado(enr: EnriquecimentoData | null | undefined, cpf: string): boolean {
  const alvo = onlyDigits(cpf);
  const s = (enr?.socios_enriquecidos || []).find((e) => onlyDigits(e.cpf) === alvo);
  return !!s && (!!s.pessoa || (s.telefones?.length || 0) > 0 || (s.emails?.length || 0) > 0);
}

/**
 * Não existe mais o passo 'escolher_dono': desde 07/08/2026 o representante é
 * escolhido sozinho (ver `escolherRepresentante`), então empresa com 2+ sócios
 * segue direto para 'buscar_dono'. As linhas legadas com
 * `representante_pendente: true` caem aqui naturalmente — elas nunca chegaram a
 * gravar `representante`, então o fluxo normal as pega.
 */
export function proximoPassoEnriquecimento(row: StagedRow): ProximoPasso {
  const enr = row.enriquecimento_data;
  const { tipo } = docDaRow(row);

  if (tipo === 'cnpj' && !enr?.dono_nao_encontrado) {
    // Empresa (CNPJ) com representante DEFINIDO mas cujo CPF nunca foi consultado:
    // falta buscar o dono, mesmo que a linha tenha ficado marcada 'completo' por um
    // fluxo antigo (senão o modal dizia "concluído" e escondia a etapa do dono).
    // Exceto quando a Lemit já disse que não conhece nenhum sócio.
    const repCpf = onlyDigits(enr?.representante?.cpf || enr?.socio_principal?.cpf);
    if (repCpf.length === 11 && !donoConsultado(enr, repCpf)) return 'buscar_dono';
  }

  if (row.enriquecimento_status === 'completo') return 'completo';
  if (row.enriquecimento_status === 'falhou') return 'tentar_novamente';
  // 'empresa' = CNPJ já consultado, falta o CPF do dono (etapa 2).
  if (row.enriquecimento_status === 'empresa') return 'buscar_dono';
  return 'enriquecer';
}

/**
 * Consulta o CPF de um sócio e devolve o bloco enriquecido (payload completo
 * incluso). `null` = a Lemit não conhece esse CPF; qualquer outro erro sobe.
 */
async function buscarSocio(socio: LemitSocio): Promise<SocioEnriquecido | null> {
  const cpf = onlyDigits(socio.cpf);
  try {
    const r = await consultarLemit({ criterio: 'cpf', valor: cpf });
    const d = r.data as LemitPessoaData;
    return {
      cpf,
      nome: d?.nome || socio.nome,
      participacao: socio.participacao,
      data_nascimento: isoToDate(d?.data_nascimento) || undefined,
      telefones: [...(d?.celulares || []), ...(d?.fixos || [])],
      emails: d?.emails || [],
      enderecos: d?.enderecos || [],
      pessoa: d,   // payload completo p/ a seção "dados do representante"
    };
  } catch (e: any) {
    if (lemitNaoAcha(e?.message)) return null;
    throw e;
  }
}

/** Escolha manual: aplica o sócio mesmo sem os dados da Lemit (fica só nome/CPF). */
async function consultarSocio(socio: LemitSocio): Promise<SocioEnriquecido> {
  try {
    const s = await buscarSocio(socio);
    if (s) return s;
  } catch (e: any) {
    // Limite Lemit precisa propagar; senão a empresa fecharia sem os dados do sócio.
    if (isLemitLimitError(e?.message)) throw e;
  }
  return { cpf: onlyDigits(socio.cpf), nome: socio.nome, participacao: socio.participacao, telefones: [], emails: [] };
}

/**
 * Nenhum sócio está na Lemit. Não é 'falhou': a empresa já está enriquecida, só
 * não há contato do dono. Nome e CPF do dono (pela regra de sempre) ficam como
 * representante — servem para o termo mesmo sem telefone.
 */
function patchDonoNaoEncontrado(base: StagedRow, cpfs: string[]): Partial<StagedRow> {
  const enr: EnriquecimentoData = {
    ...(base.enriquecimento_data || {}),
    cpfs_nao_encontrados: cpfs,
    dono_nao_encontrado: true,
  };
  const { socio, criterio } = escolherRepresentante(enr.socios || []);
  const cpf = onlyDigits(socio?.cpf);
  if (socio && cpf.length === 11) {
    enr.socio_principal = { cpf, nome: socio.nome, participacao: socio.participacao };
    enr.representante = { nome: socio.nome, cpf };
    enr.representante_pendente = false;
    enr.representante_criterio = criterio;
  }
  return { enriquecimento_data: enr, enriquecimento_status: 'completo' };
}

/**
 * Aplica um sócio já consultado como representante e fecha o enriquecimento.
 * `criterio` registra COMO a escolha saiu ('manual' quando veio do modal).
 */
async function aplicarRepresentante(
  base: StagedRow,
  socioEnr: SocioEnriquecido,
  criterio: CriterioRepresentante | 'manual' = 'manual',
): Promise<StagedRow> {
  const donoTel = bestPhone(socioEnr.telefones);
  const donoMail = bestEmail(socioEnr.emails);
  const enr: EnriquecimentoData = {
    ...(base.enriquecimento_data || {}),
    socios_enriquecidos: [socioEnr],
    socio_principal: { cpf: socioEnr.cpf, nome: socioEnr.nome, participacao: socioEnr.participacao },
    representante: { nome: socioEnr.nome, cpf: socioEnr.cpf, data_nascimento: socioEnr.data_nascimento },
    representante_pendente: false,
    representante_criterio: criterio,
  };
  if (donoTel) enr.melhor_whatsapp = phoneDigits(donoTel);
  if (donoMail?.email) enr.melhor_email = donoMail.email;

  const patch: Partial<StagedRow> = {
    enriquecimento_data: enr,
    enriquecimento_status: 'completo',
    telefone: phoneDigits(donoTel) || base.telefone,
  };
  await salvarEnriquecimento(base.place_id, patch);

  // Se a linha JÁ foi importada, propaga o representante para a negociação —
  // senão o dado ficaria preso na lista fria e o lead no CRM seguiria sem dono.
  if (base.negociacao_id && onlyDigits(socioEnr.cpf).length === 11) {
    const updates: Record<string, any> = {
      nome_representante_legal: socioEnr.nome ?? null,
      cpf_representante: onlyDigits(socioEnr.cpf),
    };
    if (socioEnr.data_nascimento) updates.data_nascimento_representante = socioEnr.data_nascimento;
    await supabase.from('negociacoes').update(updates).eq('id', base.negociacao_id);
  }

  return { ...base, ...patch };
}

/**
 * Etapa 2 — busca o contato do DONO (só CNPJ).
 *
 * Desde 07/08/2026 o representante é escolhido sozinho mesmo com 2+ sócios
 * (`escolherRepresentante`: maior participação → capital → primeiro da lista).
 * A regra anterior parava e perguntava, o que deixou 764 empresas encalhadas
 * esperando alguém clicar uma a uma. A troca segue disponível no modal
 * (`definirRepresentanteRow`).
 */
export async function enriquecerSociosRow(rowTela: StagedRow): Promise<StagedRow> {
  const row = await garantirCompleta(rowTela);
  const { tipo } = docDaRow(row);
  let base = row;

  if (tipo === 'cpf') {
    // CPF já é o contato final — nada a fazer além da etapa 1.
    if (base.enriquecimento_status !== 'completo') base = await enriquecerEmpresaRow(base);
    return base;
  }

  // garante que temos a lista de sócios (vem do retorno da empresa)
  if (!base.enriquecimento_data?.socios?.length) {
    base = await enriquecerEmpresaRow(base);
  }
  const socios = base.enriquecimento_data?.socios || [];
  const naoEncontrados = (base.enriquecimento_data?.cpfs_nao_encontrados || []).map(onlyDigits);

  // Tenta os sócios em ordem até a Lemit conhecer um. Cada "não encontrado" é
  // gravado na hora, para nunca ser pago de novo.
  for (const { socio, criterio } of candidatosDono(socios, naoEncontrados)) {
    const enr = await buscarSocio(socio);
    if (enr) return aplicarRepresentante(base, enr, criterio);
    naoEncontrados.push(onlyDigits(socio.cpf));
    const patch: Partial<StagedRow> = {
      enriquecimento_data: { ...(base.enriquecimento_data || {}), cpfs_nao_encontrados: [...naoEncontrados] },
    };
    await salvarEnriquecimento(base.place_id, patch);
    base = { ...base, ...patch };
  }

  // Sem sócio com CPF, ou nenhum está na Lemit: acabou o que dava para fazer.
  const patch: Partial<StagedRow> = naoEncontrados.length
    ? patchDonoNaoEncontrado(base, naoEncontrados)
    : { enriquecimento_status: 'completo' };
  await salvarEnriquecimento(base.place_id, patch);
  return { ...base, ...patch };
}

/**
 * Executa UM passo do enriquecimento — nunca encadeia sozinho, para o custo ser
 * sempre previsível (cada clique = uma etapa):
 *   • não enriquecido / falhou → consulta o documento (CPF = pessoa · CNPJ = empresa)
 *   • CNPJ já consultado       → busca o CPF do dono (1 sócio) ou marca para escolha (2+)
 *   • completo                 → não faz nada (não repete consulta paga)
 */
export async function enriquecerRow(row: StagedRow): Promise<StagedRow> {
  const passo = proximoPassoEnriquecimento(row);
  if (passo === 'completo') return row;
  if (passo === 'buscar_dono') return enriquecerSociosRow(row);
  return enriquecerEmpresaRow(row);   // etapa 1 e PARA (o dono é um passo à parte)
}

/**
 * Define manualmente o representante (empresas com 2+ sócios) e JÁ consulta o CPF
 * escolhido — é o passo que faltava para fechar como `completo`.
 */
export async function definirRepresentanteRow(row: StagedRow, socio: LemitSocio): Promise<StagedRow> {
  return aplicarRepresentante(await garantirCompleta(row), await consultarSocio(socio));
}

// ── Importação para o CRM (lead / rota) ────────────────────────────────────────
// Espelha o fluxo de ProspeccaoGeoView.importarCore/importarComoRota, mas para a
// lista fria (PJ/PF) com documento, e-mail e melhor WhatsApp do dono.

export interface FirstStage { name: string; pipeline_id: string; }

/** Registra a mensagem de erro do último import (alimenta o filtro "teve erros"). */
export async function marcarErroImport(placeId: string, msg: string | null): Promise<void> {
  await supabase.from('empresas_prospectadas')
    .update({ import_erro: msg, updated_at: new Date().toISOString() })
    .eq('place_id', placeId);
}

/** Melhor endereço (cidade/bairro) já disponível: Lemit enriquecido → staged. */
function melhorCidadeBairro(row: StagedRow): { cidade: string | null; bairro: string | null } {
  const enr = row.enriquecimento_data;
  const end = enr?.empresa?.endereco || enr?.pessoa?.endereco || null;
  return {
    cidade: (end?.cidade || row.cidade) ?? null,
    bairro: (end?.bairro || row.bairro) ?? null,
  };
}

/** Cria a negociação a partir da linha encenada e marca a empresa como importada. */
export async function importarStagedLead(
  row: StagedRow,
  responsavelId: string,
  importadoPor: string,
  firstStage: FirstStage,
  /** A Busca Geográfica importa a mesma linha, mas registra a origem dela. */
  origem = 'Lista Fria (Upload)',
): Promise<number | null> {
  // Representante, endereço da Lemit e contatos do dono não vêm na lista enxuta
  row = await garantirCompleta(row);
  const { doc, tipo } = docDaRow(row);
  const enr = row.enriquecimento_data;
  const wpp = enr?.melhor_whatsapp || row.telefone || null;
  const mail = enr?.melhor_email || row.email || null;
  const { cidade, bairro } = melhorCidadeBairro(row);
  const isPJ = tipo === 'cnpj';
  // Com 2+ sócios (representante_pendente) NÃO chutamos o representante — fica vazio
  // até alguém escolher na UI, senão o termo sairia com o sócio errado.
  const rep = enr?.representante_pendente ? undefined : enr?.representante;
  const clienteNome = isPJ ? (row.razao_social || row.nome) : (enr?.pessoa?.nome || row.nome);

  const insert: Record<string, any> = {
    titulo: row.nome,
    cliente_nome: clienteNome,
    stage: firstStage.name,
    status: 'em_andamento',
    pipeline_id: firstStage.pipeline_id,
    consumo_medio_kwh: 0,
    // `categoria` da Lista Fria é a NATUREZA JURÍDICA do CNPJ BIZ ("Sociedade Empresária
    // Limitada"), não o ramo — gravada como segmento, 516 leads nasceram assim no CRM.
    // O ramo sai do CNAE; sem CNAE mapeado o campo fica vazio, que é a verdade.
    segmento: segmentoDaLinha(row) ?? undefined,
    tipo_cliente: isPJ ? 'PJ' : 'PF',
    cpf_cnpj: doc,
    email: mail,
    cidade,
    bairro,
    whatsapp: wpp,
    latitude: row.latitude,
    longitude: row.longitude,
    geocode_status: row.latitude != null ? 'endereco' : 'pendente',
    origem,
    responsavel_id: responsavelId,
  };
  if (isPJ && rep) {
    if (rep.nome) insert.nome_representante_legal = rep.nome;
    if (rep.cpf) insert.cpf_representante = onlyDigits(rep.cpf);
    if (enr?.representante?.data_nascimento) insert.data_nascimento_representante = enr.representante.data_nascimento;
  }

  const { data: neg, error } = await supabase
    .from('negociacoes')
    .insert([insert])
    .select('id')
    .single();
  if (error) throw error;

  await supabase.from('empresas_prospectadas')
    .update({ status: 'importado', negociacao_id: neg.id, importado_por: importadoPor, import_erro: null, updated_at: new Date().toISOString() })
    .eq('place_id', row.place_id);
  return neg.id as number;
}

/** Importa um grupo de leads e cria uma rota agendada com as visitas (1 rota / responsável). */
export async function criarRotaComLeads(
  grupo: StagedRow[],
  responsavelId: string,
  solicitanteId: string,
  firstStage: FirstStage,
  config: { nome: string; data: string; prioridade: string; observacoes?: string | null },
  nomeRota: string,
): Promise<number> {
  const paradas: Array<{ negId: number; tarefaId: number | null }> = [];
  for (const row of grupo) {
    const negId = await importarStagedLead(row, responsavelId, solicitanteId, firstStage);
    if (!negId) continue;
    const { data: tarefa } = await supabase.from('tarefas').insert([{
      titulo: `Visita — ${row.nome}`,
      descricao: config.observacoes || null,
      responsavel_id: responsavelId,
      solicitante_id: solicitanteId,
      prioridade: config.prioridade,
      vencimento: config.data,
      status: 'a_iniciar',
      negociacao_id: negId,
      tipo: 'prospeccao',
      objetivo: `Rota: ${config.nome}`,
      created_at: new Date().toISOString(),
    }]).select('id').single();
    paradas.push({ negId, tarefaId: tarefa?.id ?? null });
  }
  if (paradas.length === 0) return 0;

  const { data: rota, error: rotaErr } = await supabase
    .from('rotas_comerciais')
    .insert([{
      vendedor_id: responsavelId,
      data: config.data,
      origem: 'mapa',
      status: 'agendada',
      nome: nomeRota,
      prioridade: config.prioridade,
      observacoes: config.observacoes || null,
    }])
    .select()
    .single();
  if (rotaErr) throw rotaErr;

  const { error: parErr } = await supabase.from('rota_paradas').insert(
    paradas.map((p, i) => ({ rota_id: rota.id, posicao: i + 1, negociacao_id: p.negId, tarefa_id: p.tarefaId })),
  );
  if (parErr) throw parErr;
  return paradas.length;
}

/**
 * Importa os leads e agenda a visita (tarefa) de cada um na data escolhida — sem criar rota.
 * A rota vira artefato do dia (Rotas → Agenda → "Criar Rota Inteligente"), unificando o fluxo.
 */
export async function agendarVisitasComLeads(
  grupo: StagedRow[],
  responsavelId: string,
  solicitanteId: string,
  firstStage: FirstStage,
  config: { data: string; prioridade: string; observacoes?: string | null },
): Promise<number> {
  let visitas = 0;
  for (const row of grupo) {
    const negId = await importarStagedLead(row, responsavelId, solicitanteId, firstStage);
    if (!negId) continue;
    const { error } = await supabase.from('tarefas').insert([{
      titulo: `Visita — ${row.nome}`,
      descricao: config.observacoes || null,
      responsavel_id: responsavelId,
      solicitante_id: solicitanteId,
      prioridade: config.prioridade,
      vencimento: config.data,
      status: 'a_iniciar',
      negociacao_id: negId,
      tipo: 'prospeccao',
      modalidade: 'presencial',
      created_at: new Date().toISOString(),
    }]);
    if (!error) visitas++;
  }
  return visitas;
}
